import React, { useCallback, useEffect, useState } from "react";

import { makeStyles } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import Grid from "@material-ui/core/Grid";
import TextField from "@material-ui/core/TextField";
import MenuItem from "@material-ui/core/MenuItem";
import Typography from "@material-ui/core/Typography";
import Tooltip from "@material-ui/core/Tooltip";

import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  ResponsiveContainer,
  Tooltip as ChartTooltip,
} from "recharts";
import InsightsIcon from "@material-ui/icons/TrendingUp";
import WarningIcon from "@material-ui/icons/ReportProblemOutlined";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import MainContainer from "../../components/MainContainer";
import PageHeader from "../../components/PageHeader";
import DataTable from "../../components/DataTable";

const fmtMoney = (v) =>
  Number(v || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

const fmtNum = (v) => Number(v || 0).toLocaleString("pt-BR");

const fmtDay = (iso) => {
  const [, m, d] = String(iso).split("-");
  return `${d}/${m}`;
};

const primeiroDiaDoMes = () => {
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().slice(0, 10);
};

const useStyles = makeStyles((theme) => ({
  body: { flex: 1, minHeight: 0, overflow: "auto", paddingTop: theme.spacing(2) },
  tile: { padding: theme.spacing(2), height: "100%" },
  tileLabel: {
    fontSize: 11,
    textTransform: "uppercase",
    letterSpacing: "0.04em",
    color: theme.palette.text.secondary,
  },
  tileValue: { fontSize: 26, fontWeight: 700, lineHeight: 1.15 },
  tileHint: { fontSize: 12, color: theme.palette.text.secondary },
  positive: { color: theme.palette.success.main },
  negative: { color: theme.palette.error.main },
  chartCard: { padding: theme.spacing(2), marginTop: theme.spacing(2) },
  chartBox: { height: 240, marginTop: theme.spacing(1) },
  num: { fontVariantNumeric: "tabular-nums" },
  money: { fontVariantNumeric: "tabular-nums", fontWeight: 600 },
  swatch: {
    display: "inline-block",
    width: 10,
    height: 10,
    borderRadius: "50%",
    marginRight: 6,
    verticalAlign: "middle",
    background: theme.palette.text.disabled,
  },
  alert: {
    display: "flex",
    alignItems: "flex-start",
    gap: theme.spacing(1.5),
    padding: theme.spacing(2),
    marginTop: theme.spacing(2),
    borderRadius: theme.shape.borderRadius,
    border: `1px solid ${theme.palette.warning.main}`,
    color: theme.palette.text.primary,
  },
  alertIcon: { color: theme.palette.warning.main, flexShrink: 0 },
}));

const Tile = ({ label, value, hint, tone }) => {
  const classes = useStyles();
  const toneClass =
    tone === "positive" ? classes.positive : tone === "negative" ? classes.negative : "";
  return (
    <Paper variant="outlined" className={classes.tile}>
      <div className={classes.tileLabel}>{label}</div>
      <div className={`${classes.tileValue} ${toneClass}`}>{value}</div>
      {hint && <div className={classes.tileHint}>{hint}</div>}
    </Paper>
  );
};

/**
 * Funil por tag.
 *
 * Responde "de onde veio e o que aconteceu" — a pergunta que decide onde
 * investir. Os painéis existentes olhavam pipeline e caixa separadamente,
 * sem cruzar a origem com o desfecho.
 */
const TagFunnel = () => {
  const classes = useStyles();

  const [period, setPeriod] = useState({
    start: primeiroDiaDoMes(),
    end: new Date().toISOString().slice(0, 10),
  });
  const [tagId, setTagId] = useState("");
  const [tags, setTags] = useState([]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api
      .get("/client-tags")
      .then(({ data: d }) => setTags(Array.isArray(d) ? d : d?.tags || []))
      .catch(() => {});
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { start: period.start, end: period.end };
      if (tagId) params.tagId = tagId;
      const { data: d } = await api.get("/analytics/tag-funnel", { params });
      setData(d);
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  }, [period, tagId]);

  useEffect(() => {
    load();
  }, [load]);

  const s = data?.summary;
  const byTag = data?.byTag || [];
  const daily = data?.daily || [];

  const columns = [
    {
      key: "tagName",
      label: "Tag",
      render: (t) => (
        <Typography variant="subtitle2">
          <span
            className={classes.swatch}
            style={{ background: t.tagColor || undefined }}
          />
          {t.tagName}
        </Typography>
      ),
    },
    {
      key: "entered",
      label: "Entraram",
      align: "right",
      render: (t) => <span className={classes.num}>{fmtNum(t.entered)}</span>,
    },
    {
      key: "won",
      label: "Fecharam",
      align: "right",
      render: (t) => (
        <span className={`${classes.num} ${classes.positive}`}>{fmtNum(t.won)}</span>
      ),
    },
    {
      key: "lost",
      label: "Perderam",
      align: "right",
      render: (t) => (
        <span className={`${classes.num} ${t.lost > 0 ? classes.negative : ""}`}>
          {fmtNum(t.lost)}
        </span>
      ),
    },
    {
      key: "open",
      label: "Sem desfecho",
      align: "right",
      render: (t) => <span className={classes.num}>{fmtNum(t.open)}</span>,
    },
    {
      key: "conversionPct",
      label: "Conversão",
      align: "right",
      render: (t) => (
        <span className={classes.num}>{Number(t.conversionPct).toFixed(1)}%</span>
      ),
    },
    {
      key: "wonAmount",
      label: "Valor fechado",
      align: "right",
      render: (t) => <span className={classes.money}>{fmtMoney(t.wonAmount)}</span>,
    },
    {
      key: "receivedAmount",
      label: "Recebido",
      align: "right",
      render: (t) => (
        <Tooltip title="Contas a receber já baixadas destes clientes">
          <span className={classes.money}>{fmtMoney(t.receivedAmount)}</span>
        </Tooltip>
      ),
    },
    {
      key: "averageTicket",
      label: "Ticket médio",
      align: "right",
      render: (t) => <span className={classes.money}>{fmtMoney(t.averageTicket)}</span>,
    },
  ];

  return (
    <MainContainer>
      <PageHeader
        title="Origem e resultado"
        subtitle="De onde vieram os clientes do período e o que aconteceu com eles: quantos fecharam, quanto entrou."
        filters={
          <>
            <TextField
              type="date"
              label="De"
              InputLabelProps={{ shrink: true }}
              value={period.start}
              onChange={(e) => setPeriod((p) => ({ ...p, start: e.target.value }))}
            />
            <TextField
              type="date"
              label="Até"
              InputLabelProps={{ shrink: true }}
              value={period.end}
              onChange={(e) => setPeriod((p) => ({ ...p, end: e.target.value }))}
            />
            <TextField
              select
              label="Tag"
              style={{ minWidth: 200 }}
              value={tagId}
              onChange={(e) => setTagId(e.target.value)}
            >
              <MenuItem value="">Todas</MenuItem>
              {tags.map((t) => (
                <MenuItem key={t.id} value={t.id}>
                  {t.name}
                </MenuItem>
              ))}
            </TextField>
          </>
        }
      />

      <div className={classes.body}>
        {s && (
          <>
            <Grid container spacing={2}>
              <Grid item xs={6} md={3}>
                <Tile
                  label="Entraram"
                  value={fmtNum(s.totalEntered)}
                  hint="clientes criados no período"
                />
              </Grid>
              <Grid item xs={6} md={3}>
                <Tile
                  label="Fecharam"
                  value={fmtNum(s.totalWon)}
                  tone="positive"
                  hint={`${Number(s.conversionPct).toFixed(1)}% de conversão`}
                />
              </Grid>
              <Grid item xs={6} md={3}>
                <Tile
                  label="Sem desfecho"
                  value={fmtNum(s.totalOpen)}
                  hint={`${fmtNum(s.totalLost)} perdidos`}
                />
              </Grid>
              <Grid item xs={6} md={3}>
                <Tile
                  label="Ticket médio"
                  value={fmtMoney(s.averageTicket)}
                  hint="entre quem fechou"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Tile
                  label="Valor fechado"
                  value={fmtMoney(s.wonAmount)}
                  tone="positive"
                  hint="soma dos negócios ganhos"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Tile
                  label="Efetivamente recebido"
                  value={fmtMoney(s.receivedAmount)}
                  hint="contas já baixadas destes clientes"
                />
              </Grid>
            </Grid>

            {/* Sem tag não há como medir origem. Este aviso é o dado mais
                acionável da tela quando aparece. */}
            {s.untagged > 0 && (
              <Paper variant="outlined" className={classes.alert}>
                <WarningIcon className={classes.alertIcon} />
                <div>
                  <Typography variant="subtitle2">
                    {fmtNum(s.untagged)} de {fmtNum(s.totalEntered)} clientes entraram
                    sem nenhuma tag
                  </Typography>
                  <Typography variant="body2" className={classes.tileHint}>
                    Sem tag não dá para saber de onde vieram, e o que está abaixo
                    cobre apenas o restante. Marcar a origem na entrada é o que faz
                    esta tela responder quanto cada canal devolve.
                  </Typography>
                </div>
              </Paper>
            )}

            {daily.length > 0 && (
              <Paper variant="outlined" className={classes.chartCard}>
                <Typography variant="subtitle1" style={{ fontWeight: 600 }}>
                  Entradas por dia
                </Typography>
                <div className={classes.chartBox}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={daily.map((d) => ({ ...d, dia: fmtDay(d.date) }))}>
                      <XAxis dataKey="dia" />
                      <YAxis allowDecimals={false} />
                      <ChartTooltip
                        formatter={(v) => [fmtNum(v), "clientes"]}
                        labelFormatter={(l) => `Dia ${l}`}
                      />
                      <Bar dataKey="entered" name="Entraram" fill="#2F3F64" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </Paper>
            )}
          </>
        )}

        <DataTable
          columns={columns}
          rows={byTag}
          loading={loading}
          getRowKey={(t) => t.tagId}
          emptyIcon={<InsightsIcon />}
          emptyTitle="Nenhuma tag com movimento no período"
          emptyDescription="Marque a origem dos clientes com tags (instagram, indicação, tráfego pago) para esta tela mostrar o que cada canal devolve."
        />
      </div>
    </MainContainer>
  );
};

export default TagFunnel;
